import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import useSWR from 'swr';
import Loading from '../../assets/icons/Loading.png';
import KakaoIcon from '../../assets/icons/login-v2/kakao.svg';
import GoogleIcon from '../../assets/icons/login-v2/google.svg';
import AppleIcon from '../../assets/icons/login-v2/apple.svg';
import AuthShell from '../../components/login-v2/AuthShell';
import AuthMascot from '../../components/login-v2/AuthMascot';
import SnsButton from '../../components/login-v2/SnsButton';
import ModalDialog from '../../components/common/Modal';
import Button from '../../components/common/Button';
import ToastAlert from '../../components/common/Toast';
import { AuthContext } from '../../context/AuthContext';
import { isDevelopmentMode, useScript } from '../../utils/hooks';
import { useUnityGameStatus } from '../game/yaong-egg/GameProviderEgg';
import { isSignupOnboarding } from './signupStorage';
import { useSnsLogin } from './useSnsLogin';

const SPLASH_MS = 1000;

function isUpdateRequired(appInfo, config) {
  if (!appInfo || !config?.infos) return false;

  const currentVersion = parseFloat(`${appInfo.version}.${appInfo.buildVersion}`);
  const requiredVersion =
    appInfo.os === 'android'
      ? parseFloat(config.infos.android_version)
      : appInfo.os === 'ios'
        ? parseFloat(config.infos.ios_version)
        : NaN;

  return Number.isFinite(requiredVersion) && requiredVersion > currentVersion;
}

function LoadingIcon() {
  return (
    <div className="mt-14 flex justify-center">
      <img src={Loading} alt="loading" height={50} className="animate-spin" />
    </div>
  );
}

function SplashScreen() {
  return (
    <AuthShell className="items-center justify-center">
      <AuthMascot size="lg" />
      <p className="mt-16 tracking-[5px] font-['Pretendard'] text-[23px] font-bold leading-[1.32] text-black">
        앱테크는 <span className="text-[#8566DE]">야옹상회</span>
      </p>
    </AuthShell>
  );
}

export default function LoginV2Page() {
  const { API_SERVER_DOMAIN, useAccessToken, appInfo } = useContext(AuthContext);
  const { setGameMode } = useUnityGameStatus();
  const navigate = useNavigate();
  const token = useAccessToken();
  const urlParams = new URLSearchParams(window.location.search);
  const error = urlParams.get('err');

  /** 최소 1.5초 스플래시 노출 타이머 완료 여부 */
  const [splashMinElapsed, setSplashMinElapsed] = useState(false);
  const [alertMessage, setAlertMessage] = useState(false);

  const {
    data: config,
    error: configError,
    isLoading: isConfigLoading,
  } = useSWR(`${API_SERVER_DOMAIN}/auth/config`, (url) => fetch(url).then((res) => res.json()));

  const isDev = isDevelopmentMode();
  const updateRequired = isUpdateRequired(appInfo, config);
  const isBootstrapping = !appInfo || isConfigLoading;

  const DEV_LOGIN_TOKEN =
    isDev && process.env.REACT_APP_DEV_LOGIN_ACCESS_TOKEN && process.env.REACT_APP_DEV_LOGIN_REFRESH_TOKEN
      ? {
          accessToken: process.env.REACT_APP_DEV_LOGIN_ACCESS_TOKEN,
          accessTokenExpiresIn: Number(process.env.REACT_APP_DEV_LOGIN_ACCESS_TOKEN_EXPIRES_IN || 0),
          refreshToken: process.env.REACT_APP_DEV_LOGIN_REFRESH_TOKEN,
        }
      : null;

  const onSnsError = useCallback((message) => {
    setAlertMessage(message);
  }, []);

  const { loginWithSns, loading: snsLoading } = useSnsLogin({ onError: onSnsError });

  useScript(`${process.env.PUBLIC_URL}/flutter/flutter_script.js`);

  useEffect(() => {
    const timer = window.setTimeout(() => setSplashMinElapsed(true), SPLASH_MS);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    // 스플래시 최소 시간 + 부트스트랩 완료 후에만 리다이렉트
    if (!splashMinElapsed || isBootstrapping || !config || updateRequired) return;

    if (!error && token) {
      setGameMode('login');
      if (isSignupOnboarding()) {
        navigate('/login-v2/terms', { replace: true });
      } else {
        navigate('/playground', { replace: true });
      }
    }

    if (error === 'REREG-RULE-VIOLAT') {
      setAlertMessage('이미 탈퇴 한 아이디예요. 회원가입은 탈퇴일로부터 7일 후 가능해요.');
    }
  }, [
    splashMinElapsed,
    isBootstrapping,
    appInfo,
    config,
    error,
    token,
    updateRequired,
    navigate,
    setGameMode,
  ]);

  function loginWithDevToken() {
    if (!DEV_LOGIN_TOKEN) return;

    const now = new Date();
    localStorage.setItem(
      'accessToken',
      JSON.stringify({
        value: DEV_LOGIN_TOKEN.accessToken,
        expiry: now.getTime() + DEV_LOGIN_TOKEN.accessTokenExpiresIn,
      }),
    );
    localStorage.setItem('refreshToken', DEV_LOGIN_TOKEN.refreshToken);
    localStorage.setItem('isVerified', 'Y');
    navigate('/playground', { replace: true });
  }

  // 스플래시: 최소 2초 + 앱 정보/설정 로딩 중에는 항상 스플래시 UI 표시
  // (로딩에 2초 이상 걸려도 스플래시를 건너뛰지 않음)
  const showSplash = !splashMinElapsed || isBootstrapping;

  if (showSplash && !configError) {
    return <SplashScreen />;
  }

  if (configError || !config) {
    return (
      <AuthShell className="items-center justify-center gap-3 px-6 text-center">
        <p>앱 설정을 불러오지 못했습니다.</p>
        <p>네트워크 연결을 확인한 뒤 다시 시도해 주세요.</p>
        <button
          type="button"
          className="rounded-lg bg-primary-purple-01 px-4 py-2 text-white"
          onClick={() => window.location.reload()}
        >
          다시 시도
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell className="min-h-[100dvh]">
      <ModalDialog title="중요한 업데이트가 있어요!" className="update-modal" open={updateRequired}>
        <p>편리한 서비스 이용을 위해 최신 버전으로 업데이트 해주세요.</p>
        <div className="version">
          <div>
            현재 버전:&nbsp;{appInfo.version + '.' + appInfo.buildVersion}
          </div>
          <div>
            최신 버전:&nbsp;
            {appInfo.os === 'android' && config.infos.android_version}
            {appInfo.os === 'ios' && config.infos.ios_version}
          </div>
        </div>
        <Button
          onClick={() => {
            let targetUrl = '';
            if (appInfo.os === 'android') {
              targetUrl = 'https://play.google.com/store/apps/details?id=com.yaong.app';
            } else if (appInfo.os === 'ios') {
              targetUrl =
                'https://apps.apple.com/kr/app/%EC%95%BC%EC%98%B9%EC%83%81%ED%9A%8C-%EC%89%BD%EA%B3%A0-%EC%A6%90%EA%B1%B0%EC%9A%B4-%ED%8F%AC%EC%9D%B8%ED%8A%B8-%EB%A6%AC%EC%9B%8C%EB%93%9C%EC%95%B1-%EC%95%B1%ED%85%8C%ED%81%AC/id6502283766';
            }
            window.location.href = targetUrl;
          }}
        >
          업데이트 하러가기
        </Button>
      </ModalDialog>

      <div className="flex h-full min-h-[100dvh] flex-1 flex-col px-[10px] pb-[max(2rem,env(safe-area-inset-bottom))] pt-[120px]">
        <div className="flex flex-col items-center">
          <p className="text-center font-['Pretendard'] text-[30px] font-bold leading-[1.32] tracking-[5px]">
            놀수록,
            <br />
            <span className="text-[#8566DE]">쌓인다.</span>
          </p>
          <AuthMascot size="lg" className="mt-16" />
        </div>

        <div className="mt-auto flex w-full flex-col gap-[5px] pt-12 pb-12">
          {snsLoading ? (
            <LoadingIcon />
          ) : (
            <>
              <SnsButton
                variant="kakao"
                icon={KakaoIcon}
                label="카카오로 계속하기"
                disabled={updateRequired}
                onClick={() => loginWithSns('kakao')}
              />
              <SnsButton
                variant="google"
                icon={GoogleIcon}
                label="구글로 계속하기"
                disabled={updateRequired}
                onClick={() => loginWithSns('google')}
              />
              <SnsButton
                variant="apple"
                icon={AppleIcon}
                label="Apple로 계속하기"
                disabled={updateRequired}
                onClick={() => loginWithSns('apple')}
              />
              {DEV_LOGIN_TOKEN ? (
                <SnsButton variant="google" icon={null} label="개발용 토큰 로그인" onClick={loginWithDevToken} />
              ) : null}
            </>
          )}
        </div>
      </div>

      <ToastAlert show={!!alertMessage} onClose={setAlertMessage} text={alertMessage} />
    </AuthShell>
  );
}
